import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import HeroOrbit from "@/components/HeroOrbit";
import ParticleField from "@/components/ParticleField";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import UniverseGraph from "@/components/UniverseGraph";
import { useProjects } from "@/lib/useProjects";
import { useContent } from "@/context/ContentContext";

const INDUSTRIES = ["AI", "Nightlife", "Hospitality", "Culinary", "Fintech", "Creator Economy", "Virtual Worlds", "Automation"];

const PILLARS = [
  { t: "Imagine", lines: ["Challenge what already exists.", "Question limitations.", "See opportunities others overlook."] },
  { t: "Build", lines: ["Turn ideas into products.", "Turn problems into systems.", "Turn imagination into usable technology."] },
  { t: "Evolve", lines: ["Learn from users.", "Learn from data.", "Continuously improve the technology."] },
];

export default function Home() {
  const { projects } = useProjects();
  const { content } = useContent();
  const featured = projects.filter((p) => p.featured);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "ImJove Tech",
    description: "ImJove Tech builds intelligent platforms, AI products, digital ecosystems, and future innovations.",
    founder: { "@type": "Person", name: "Jove Divinity", jobTitle: "Founder & CEO" },
    slogan: "Imagine. Build. Evolve.",
  };

  return (
    <div>
      <SEO
        title="ImJove Tech — Building Technology for the Next Era"
        description="ImJove Tech develops intelligent platforms and digital experiences designed to transform industries and create entirely new ones. Founded by Jove Divinity."
        image={content.logo_url}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative flex min-h-screen items-center overflow-hidden pt-28">
        <ParticleField className="absolute inset-0 z-0" density={50} />
        <div className="mx-auto grid w-full max-w-[1400px] items-center gap-10 px-6 md:px-12 lg:grid-cols-2">
          <div className="relative z-10">
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="font-mono2 text-xs uppercase tracking-brand text-gold-light"
            >
              ImJove Tech
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
              className="mt-5 font-display text-5xl font-bold leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl"
            >
              WE'RE BUILDING<br />
              <span className="text-gold-gradient">WHAT'S NEXT.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-7 max-w-lg font-body text-lg font-light text-white/70"
            >
              Technology designed around people, possibility, and the future.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.35 }}
              className="mt-4 max-w-lg font-body text-sm text-white/45"
            >
              ImJove Tech develops intelligent platforms and digital experiences designed to transform industries and create entirely new ones.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.45 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link to="/technology" data-testid="hero-cta-technology" className="btn-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-mono2 text-[12px] font-semibold uppercase tracking-widest">
                Explore Our Technology <ArrowRight size={15} />
              </Link>
              <Link to="/founder" data-testid="hero-cta-founder" className="btn-ghost inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-mono2 text-[12px] uppercase tracking-widest">
                Meet Jove Divinity
              </Link>
            </motion.div>
          </div>

          <div className="relative z-10 hidden h-[520px] lg:block">
            <HeroOrbit />
          </div>
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-ink-900 to-transparent" />
      </section>

      {/* ECOSYSTEM */}
      <section className="relative py-28" id="ecosystem">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono2 text-xs uppercase tracking-brand text-gold-light">The ImJove Ecosystem</p>
            <h2 className="mt-4 max-w-3xl font-display text-4xl font-bold tracking-tighter text-white sm:text-5xl">
              One company. <span className="text-silver-gradient">Multiple industries.</span>
            </h2>
            <p className="mt-4 font-body text-lg text-white/50">Different industries. One vision.</p>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <ProductCard project={p} />
              </Reveal>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/technology" className="btn-ghost inline-flex items-center gap-2 rounded-full px-6 py-3 font-mono2 text-[11px] uppercase tracking-widest">
              View All Companies <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* INDUSTRIES marquee */}
      <section className="relative overflow-hidden border-y border-white/10 bg-ink-800 py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 className="max-w-3xl font-display text-3xl font-bold tracking-tighter text-white sm:text-4xl">
              Technology without industry limits.
            </h2>
            <p className="mt-4 max-w-xl font-body text-white/50">
              We don't build technology for one industry. We build technology around opportunity.
            </p>
          </Reveal>
        </div>
        <div className="mt-14 flex select-none overflow-hidden">
          <div className="animate-marquee flex shrink-0 items-center gap-8 pr-8">
            {[...INDUSTRIES, ...INDUSTRIES].map((it, i) => (
              <div key={i} className="flex items-center gap-8">
                <span className="font-display text-3xl font-light text-white/25 transition-colors hover:text-gold-light sm:text-4xl">{it}</span>
                <span className="h-1.5 w-1.5 rounded-full bg-gold/50" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UNIVERSE teaser */}
      <section className="relative py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
              <div>
                <p className="font-mono2 text-xs uppercase tracking-brand text-gold-light">The ImJove Universe</p>
                <h2 className="mt-4 max-w-2xl font-display text-4xl font-bold tracking-tighter text-white sm:text-5xl">
                  Every technology, <span className="text-gold-gradient">interconnected.</span>
                </h2>
              </div>
              <Link to="/universe" className="btn-ghost inline-flex items-center gap-2 rounded-full px-6 py-3 font-mono2 text-[11px] uppercase tracking-widest">
                Open Full Universe <ArrowUpRight size={14} />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10">{projects.length > 0 && <UniverseGraph projects={projects} height={520} />}</div>
          </Reveal>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="relative border-y border-white/10 bg-ink-800 py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono2 text-xs uppercase tracking-brand text-gold-light">Company Philosophy</p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.1}>
                <div className="card-hover h-full rounded-2xl border border-white/10 bg-ink-900 p-8">
                  <span className="font-mono2 text-xs text-white/30">0{i + 1}</span>
                  <h3 className="mt-4 font-display text-4xl font-bold tracking-tighter text-gold-gradient">{p.t}</h3>
                  <ul className="mt-6 space-y-2.5">
                    {p.lines.map((l) => (
                      <li key={l} className="font-body text-white/60">{l}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-14 text-center font-display text-2xl font-light uppercase tracking-brand text-white sm:text-3xl">
              Imagine. Build. Evolve.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FINAL cinematic */}
      <section className="relative flex min-h-[80vh] items-center overflow-hidden py-32">
        <ParticleField className="absolute inset-0 z-0" density={40} />
        <div className="radial-fade absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 text-center md:px-12">
          <Reveal>
            <p className="font-mono2 text-xs uppercase tracking-brand text-white/40">The next era won't build itself.</p>
            <h2 className="mt-6 font-display text-6xl font-bold leading-none tracking-tighter text-white sm:text-8xl lg:text-[9rem]">
              WE'RE <span className="text-gold-gradient">BUILDING IT.</span>
            </h2>
            <p className="mt-10 font-display text-sm font-semibold uppercase tracking-brand text-white">ImJove Tech</p>
            <p className="mt-2 font-mono2 text-xs uppercase tracking-[0.3em] text-gold-light/80">Imagine. Build. Evolve.</p>
            <Link to="/universe" className="btn-gold mt-10 inline-flex items-center gap-2 rounded-full px-8 py-4 font-mono2 text-[12px] font-semibold uppercase tracking-widest">
              Explore the ImJove Universe <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
